// src/features/tasks/hooks/useCompletionReceipt.js
import i18n from 'i18next';
import { useState, useCallback } from 'react';
import { completionReceiptAPI } from '../services/completionReceipt';
import { useApi } from '../../../hooks/api/useApi';

export function useCompletionReceipt() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const { execute } = useApi();

  /**
   * Fetch completion receipt for a task
   * @param {string} taskId - The task ID
   * @returns {Promise<Object>} The receipt data
   */
  const fetchReceipt = useCallback(async (taskId) => {
    setLoading(true);
    setError(null);

    try {
      const result = await execute(
        () => completionReceiptAPI.getReceipt(taskId),
        { showErrorToast: false }
      );

      if (result.success) {
        setReceipt(result.data.receipt);
        return result.data.receipt;
      } else {
        setError(result.error);
        return null;
      }
    } catch (err) {
      const errorMsg = err.message || 'Failed to fetch completion receipt';
      setError(errorMsg);
      return null;
    } finally {
      setLoading(false);
    }
  }, [execute]);

  /**
   * Sign a completion receipt
   * @param {string} taskId - The task ID
   * @param {string} password - User's password for verification
   * @param {string} [notes] - Optional notes about the signature
   * @returns {Promise<Object>} Result with success flag and receipt/error
   */
  const signReceipt = useCallback(async (taskId, staffMemberId, credential, notes = null) => {
    setLoading(true);
    setError(null);

    try {
      const result = await execute(
        () => completionReceiptAPI.signReceipt(
          taskId,
          {
            staff_member_id: staffMemberId,
            ...credential,
            notes,
          }
        ),
        { showErrorToast: false }
      );

      if (result.success) {
        setReceipt(result.data.receipt);
        return {
          success: true,
          receipt: result.data.receipt,
        };
      } else {
        // Known backend codes get a translated message; others keep the server text
        const errorMsg = result.errorCode
          ? i18n.t(`tasks:completion_receipt.errors.${result.errorCode}`, { defaultValue: result.error })
          : (result.error || i18n.t('tasks:completion_receipt.signature_failed'));
        setError(errorMsg);
        return {
          success: false,
          error: errorMsg,
          errorCode: result.errorCode,
        };
      }
    } catch (err) {
      const errorMsg = err.message || 'Failed to sign receipt';
      setError(errorMsg);
      return {
        success: false,
        error: errorMsg,
      };
    } finally {
      setLoading(false);
    }
  }, [execute]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Clear receipt state
   */
  const clearReceipt = useCallback(() => {
    setReceipt(null);
  }, []);

  return {
    receipt,
    loading,
    error,
    fetchReceipt,
    signReceipt,
    clearError,
    clearReceipt,
  };
}
